import { Injectable, OnInit, OnDestroy, NgZone } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Subscription } from 'rxjs';
import { map } from 'rxjs/operators';

import { AuthenticatedResult, OidcSecurityService} from 'angular-auth-oidc-client';
import { SettingsService } from './settings.service';
import { StorageManagerService } from './storage-manager.service'
import { Utilities } from './utilities';
import { IUserLoginData, IUserLoginApplication } from '../models/index';

@Injectable()
export class AuthenticationService implements OnInit, OnDestroy {

  isAuthorized: boolean;
  logoutStarted = false;
  subscription: Subscription;

	constructor (private oidcSecurityService: OidcSecurityService, private http: HttpClient,
		private settingsService: SettingsService, private storageManagerService: StorageManagerService, private ngzone: NgZone) {

	}

	ngOnInit() {
    this.subscription = this.oidcSecurityService.isAuthenticated$.subscribe(
			(authenticatedResult: AuthenticatedResult) => {
				this.isAuthorized = authenticatedResult?.allConfigsAuthenticated[0].isAuthenticated;
      });
  }

	ngOnDestroy(): void {
		if (this.subscription) {
			this.subscription.unsubscribe();
    }
	}

	getIsAuthorized(): Observable<boolean> {
    // get AuthenticatedResult isAuthorized boolean
    return this.oidcSecurityService.isAuthenticated$.pipe(
      map((authenticatedResult: AuthenticatedResult) => {
        return authenticatedResult?.allConfigsAuthenticated[0].isAuthenticated;
      })
    );
  }

	getUserData(): Observable<any> {
    return this.oidcSecurityService.userData$;
	}

	login() {
		console.log('start login');
		this.oidcSecurityService.authorize();
	}

	refreshSession() {
		console.log('start refreshSession');
    this.oidcSecurityService.forceRefreshSession().subscribe((result) => console.log(result));
	}

  logout() {
    console.log('start logoff');
    this.logoutStarted = true;
   this.oidcSecurityService.logoffLocal();
  this.oidcSecurityService.getConfiguration().subscribe((config) => {
    const endSessionEndpoint = config.authWellknownEndpoints?.endSessionEndpoint;
    if (endSessionEndpoint) {
      this.oidcSecurityService.getIdToken().subscribe((idToken) => {
        const redirectUrl = encodeURIComponent(config.postLogoutRedirectUri || `${window.location.origin}/home`);
        let logoutUrl = `${endSessionEndpoint}?post_logout_redirect_uri=${redirectUrl}`;
        if (idToken) {
          logoutUrl += `&id_token_hint=${idToken}`;
        }
        window.location.href = logoutUrl;
      });
    }
  });
}

  // Logout
  public captureLogoutEvent(displayableId: string) {
    return this.get(`${ window.location.origin }/api/user/logout?loginName=${ displayableId }&appName=SingleSource`);
  }

	getBearerToken(): string {
        let token; 
        this.oidcSecurityService.getAccessToken().subscribe(accessToken => {
            token = accessToken;
        });
        if (token === '') { return ''; }

        return 'Bearer ' + token;
    }

  get(url: string): Observable<any> {

    return this.http.get<any>(url, { headers: this.getHeaders() });
  }

  post(url: string, data: any): Observable<any> {

    const body = JSON.stringify(data);
    return this.http.post<any>(url, body, { headers: this.getHeaders() });
  }

  private getHeaders(setContentType?: boolean, isApplicationLevel?: boolean) {

    let authData: IUserLoginData = this.getAuthData();

    if (!authData) {
      authData = authData ? authData : this.storageManagerService.read('userData');

      if (!authData) {
        this.getUserData().subscribe(res => {
          if (res) {
            this.storageManagerService.write('userData', res);
            authData = this.getAuthData();
          }
        });
      }
    } else {

      let applicationVariantId = authData.applicationVariantId ? authData.applicationVariantId.toString() : '';
      let customer = this.storageManagerService.read('currentCustomer');
      const applicationName = authData.applicationName;
      const applicationVariantName = authData.applicationVariantName;

      if (customer && !Utilities.TestIsObjectEmpty(customer)) {

        if (typeof customer !== 'object') {
          customer = JSON.parse(customer);
        }
        authData.customerGuid = customer.customerGuid.toString();
        if (customer.applicationVariants.length === 1) {
          applicationVariantId = customer.applicationVariants[0].applicationVariantId.toString();
        }
      } else {
        authData.customerGuid = '';
      }

      let headers;
      if (isApplicationLevel) {
        headers = new HttpHeaders(
          {
            'customerGuid': authData.customerGuid ? authData.customerGuid : '',
            'applicationName': applicationName
          });
      } else {
        headers = new HttpHeaders(
          {
            'customerGuid': authData.customerGuid ? authData.customerGuid : '',
            'applicationVariantId': applicationVariantId,
            'applicationName': applicationName,
            'applicationVariantName': applicationVariantName
          });
      }

      if (setContentType) {
        headers = headers.set('content-type', 'application/json');
      }

      return this.appendAuthHeader(headers);
    }
  }

  private appendAuthHeader(headers: HttpHeaders) {

    let token = '';
    this.oidcSecurityService.getAccessToken().subscribe(accessToken => {
        token = accessToken;
      });
    if (token === '') { return headers; }
    const tokenValue = 'Bearer ' + token;
    headers = headers.set('authorization', tokenValue);

    return headers;
  }

  public getAuthData(): any {
    let applications: IUserLoginApplication[];
    let appVariantId: number;
    let authData: IUserLoginData;
    let appVariantName: string;
    let applicationName: string;

    authData = this.storageManagerService.read('userData');

    if (authData) {

      if (authData.applications && typeof authData.applications !== 'object') {
        applications = JSON.parse(authData.applications as any);
      }

      if (applications && applications.length > 0) {

        const dataScope = this.storageManagerService.read('dataScope');

        if (dataScope) {

          appVariantId = +applications.find(a => a.AVName.toLowerCase() === dataScope.toLowerCase()).AVId;
          appVariantName = applications.find(a => a.AVName.toLowerCase() === dataScope.toLowerCase()).AVName.toLowerCase();
          applicationName = applications[0].AName;
        } else {
          // temporarily until the spike is resolved
          appVariantId = 251;
          applicationName = 'SingleSource';
          appVariantName = 'eudamed';
        }

        if (authData.userguid && appVariantId) {

          authData.userGuid = authData.userguid;
          authData.applicationVariantId = appVariantId.toString();
          authData.applicationVariantName = appVariantName;
          authData.applicationName = applicationName;
        }
      }
      return authData;
    }
  }

  public setHeader(setContentType?: boolean, isApplicationLevel?: boolean): HttpHeaders {

    return this.getHeaders(setContentType, isApplicationLevel);
  }

  initPendo(userguid, customerName) {
    this.ngzone.runOutsideAngular(function () {
      (function (apiKey) {
        (function (p, e, n, d, o) {

          let v, w, x, y, z; o = p[d] = p[d] || {}; o._q = [];

          v = ['initialize', 'identify', 'updateOptions', 'pageLoad'];

          for (w = 0, x = v.length; w < x; ++w) {

            (function (m) {
              o[m] = o[m] || function () { o._q[m === v[0] ? 'unshift' : 'push']([m].concat([].slice.call(arguments, 0))); };
            })(v[w]);
          }

          y = e.createElement(n); y.async = !0; y.src = 'https://cdn.pendo.io/agent/static/' + apiKey + '/pendo.js';
          z = e.getElementsByTagName(n)[0]; z.parentNode.insertBefore(y, z);
        })(window, document, 'script', 'pendo');

        // Please use Strings, Numbers, or Bools for value types.
        window['pendo'].initialize({
          visitor: {
            id: userguid  // Required if user is logged in
          },

          account: {
            id: customerName

            // You can add any additional account level key-values here,
            // as long as it's not one of the above reserved names.
          }
        });
      })('d61d92d8-e1ef-4a59-4f1f-3f8c9f94cbd8');
    });
  }

  // Set script element attribute
  setScriptAttribute(srcJs, attClass, attName) {
    this.ngzone.runOutsideAngular(function () {
      (function (srcJs, attClass, attName) {
        (function (d) {

          var sourceScripts = d.getElementsByTagName('script');

          for (var i = 0; i < sourceScripts.length; i++) {
            if (sourceScripts[i].src.indexOf(srcJs) != -1) {
              sourceScripts[i].removeAttribute(attName);
              sourceScripts[i].setAttribute(attName, attClass);
            }
          }
        })(document);
      })(srcJs, attClass, attName);
    });
  }
}
